import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  validateOfflineCredentials,
  setOfflineAuth,
  OFFLINE_AUTH_CONFIG
} from '../config/offlineAuth'

interface LoginResponse {
  message: string
  user: {
    id: string
    email: string
    name: string
    role: string
  }
  accessToken: string
}

interface LoginError {
  statusCode?: number
  message: string
}

export function Login() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  })
  const [errors, setErrors] = useState<{
    email?: string
    password?: string
    general?: string
  }>({})
  const [isLoading, setIsLoading] = useState(false)

  const validateForm = () => {
    const newErrors: typeof errors = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    // Try offline auth FIRST (for demo credentials when backend is unavailable)
    if (OFFLINE_AUTH_CONFIG.enabled) {
      const offlineUser = validateOfflineCredentials(formData.email, formData.password)
      if (offlineUser) {
        // Success with offline credentials
        setOfflineAuth(offlineUser)
        navigate('/presentations') // Go directly to presentations for offline demo
        setIsLoading(false)
        return
      }
    }

    // Try backend auth
    try {
      const response = await fetch('http://localhost:3000/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe
        })
      })

      const data: LoginResponse | LoginError = await response.json()

      if (!response.ok) {
        const error = data as LoginError

        // Handle specific error cases
        if (error.statusCode === 401) {
          setErrors({ general: 'Invalid email or password' })
        } else if (error.statusCode === 403) {
          setErrors({
            general: 'Please verify your email before logging in. Check your inbox for the verification link.'
          })
        } else if (error.statusCode === 429) {
          setErrors({
            general: 'Too many login attempts. Please wait 15 minutes before trying again.'
          })
        } else {
          setErrors({ general: error.message || 'Login failed. Please try again.' })
        }
        return
      }

      const loginData = data as LoginResponse

      // Validate that accessToken exists in response
      if (!loginData.accessToken) {
        setErrors({ general: 'Authentication token not received from server. Please try again.' })
        return
      }

      // Store access token in localStorage for authenticated API calls
      localStorage.setItem('token', loginData.accessToken)

      // Store user info in localStorage for dashboard access
      localStorage.setItem('user', JSON.stringify(loginData.user))

      // Redirect to dashboard
      navigate('/dashboard')
    } catch {
      // Network error or backend unavailable
      setErrors({
        general: OFFLINE_AUTH_CONFIG.enabled
          ? 'Network error. Backend unavailable. Try offline demo credentials: offline-demo@securesight.ai / offline-demo-2026'
          : 'Network error. Please check your connection and try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === 'rememberMe' ? (e.target as HTMLInputElement).checked : e.target.value
    }))

    // Clear field-specific error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-5">
      {/* Theme Toggle in top-right corner */}
      <div className="fixed top-4 right-4 z-50">
        {/* ThemeToggle component will be added here via App.tsx */}
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 text-foreground">
            Welcome Back
          </h1>
          <p className="text-muted-foreground">
            Sign in to your securesight.ai account
          </p>
        </div>

        <div className="bg-card p-8 rounded-lg shadow-lg border border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error Message */}
            {errors.general && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive font-medium">{errors.general}</p>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.email ? 'border-destructive' : 'border-border'
                }`}
                placeholder="you@example.com"
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange('password')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.password ? 'border-destructive' : 'border-border'
                }`}
                placeholder="••••••••"
                disabled={isLoading}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-destructive">{errors.password}</p>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleInputChange('rememberMe')}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
                disabled={isLoading}
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-foreground">
                Remember me for 7 days
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Additional Links */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <a
                href="/register"
                className="text-primary hover:underline font-medium"
              >
                Sign up
              </a>
            </p>
            <p className="text-sm">
              <a
                href="/forgot-password"
                className="text-primary hover:underline font-medium"
              >
                Forgot your password?
              </a>
            </p>
          </div>
        </div>

        {/* Demo Accounts Info */}
        <div className="mt-6 p-4 bg-muted/50 border border-border rounded-md">
          <p className="text-xs text-muted-foreground mb-2 font-medium">
            Demo Accounts (for testing):
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p className="text-primary font-semibold">• offline-demo@securesight.ai / offline-demo-2026 (No backend needed!)</p>
            <p>• superadmin@securesight.ai.com / SuperAdmin123!</p>
            <p>• admin@demo.example.com / Admin123!</p>
            <p>• user@demo.example.com / Demo123!</p>
            <p>• demo@securesight.ai.com / Demo123!</p>
          </div>
        </div>
      </div>
    </div>
  )
}
